import './styles.scoped.css'
import {Fragment, useEffect, useState} from 'react'
import {Typography, Button, TextField, Select, MenuItem, FormControl, FormHelperText, IconButton, Chip, Skeleton} from '@mui/material'
import {ArrowBack, Add, Delete} from '@mui/icons-material'
import {useForm, Controller} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {phoneRegex, emailRegex, usStates, parseError} from '../../utils/helpers'
import {useNavigate, useParams, Link as LinkRoute} from 'react-router-dom'
import {useMobile} from '../../hooks'
import {adminApi} from '../../services'
import {loadServiceCatalog} from '../../utils/serviceCatalog'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {AddressAutocompleteField, SkeletonContainer, FieldSkeleton, ButtonSkeleton, TextSkeleton, ChipSkeleton} from '../../components'

const evvChip = required =>
    required ? (
        <Chip label="EVV Required" variant="outlined" color="success" size="small" sx={{backgroundColor: '#2E7D3214'}} />
    ) : (
        <Chip label="No EVV" variant="outlined" size="small" sx={{color: 'text.secondary', borderColor: 'divider', backgroundColor: '#00000014'}} />
    )

const emptyBranch = () => ({
    branch_name: '',
    branch_code: '',
    provider_number: '',
    umpi: '',
    email: '',
    phone_number: '',
    address: {street1: '', street2: '', city: '', state: '', zip: ''},
})

const Label = ({children, required}) => (
    <Typography variant="inputLabel" color="text.secondary">
        {children}
        {required && (
            <>
                {' '}
                <Typography variant="inputLabel" color="error">
                    *
                </Typography>
            </>
        )}
    </Typography>
)

// The branch form laid out as it will be, labels and all, while the branch loads.
const FormSkeleton = ({mobile}) => {
    const fields = list =>
        list.map(([text, required]) => (
            <Fragment key={text}>
                <br />
                <br />
                <Label required={required}>{text}</Label>
                <FieldSkeleton label={false} />
            </Fragment>
        ))

    return (
        <SkeletonContainer>
            <Typography variant="overline" color="text.secondary">
                BRANCH INFORMATION
            </Typography>
            {fields([['Branch Name', true], ['Branch Code', true], ['Branch Email'], ['Branch Phone Number'], ['Branch NPI', true], ['Branch UMPI']])}
            <br />
            <br />
            <Typography variant="overline" color="text.secondary">
                ADDRESS
            </Typography>
            {fields([['Street Address Line 1', true], ['Street Address Line 2']])}
            <br />
            <br />
            <div className="form">
                <div className="form-left">
                    <Label required>City</Label>
                    <FieldSkeleton label={false} />
                </div>
                <div className="form-right">
                    <Label required>State</Label>
                    <FieldSkeleton label={false} />
                </div>
            </div>
            {fields([['Zip Code', true]])}
            <br />
            <br />
            <br />
            <Typography variant="overline" color="text.secondary">
                SERVICES
            </Typography>
            <br />
            <br />
            <div className="services-form">
                <div className="service-select">
                    <Label required>Service</Label>
                    <FieldSkeleton label={false} />
                </div>
                <ButtonSkeleton width={mobile ? '100%' : 90} height={mobile ? 42 : 56} />
            </div>
            <br />
            {[0, 1].map(index => (
                <div key={index} className="service-row">
                    <div style={{flex: 1}}>
                        <TextSkeleton width={index ? 160 : 210} fontSize={14} />
                        <div className="service-meta-row" style={{marginTop: 8}}>
                            <ChipSkeleton width={110} />
                            <TextSkeleton width={70} />
                        </div>
                        <ChipSkeleton width={96} sx={{mt: 0.75}} />
                    </div>
                    <Skeleton variant="circular" width={24} height={24} sx={{m: 1}} />
                </div>
            ))}
        </SkeletonContainer>
    )
}

export default () => {
    const {organizationId, branchId} = useParams()
    const isEdit = !!branchId
    const {mobile} = useMobile()
    const navigate = useNavigate()
    const {setLoading, setMessageBox} = useGlobalStates()
    const [organization, setOrganization] = useState(null)
    const [selectedServiceId, setSelectedServiceId] = useState('')
    const [services, setServices] = useState([])
    const [serviceCatalog, setServiceCatalog] = useState([])
    const [branchStatus, setBranchStatus] = useState('active')
    const [skeleton, setSkeleton] = useState(isEdit)

    const form = useForm({
        defaultValues: emptyBranch(),
        resolver: yupResolver(
            yup.object().shape({
                branch_name: yup.string().required('Branch name is required'),
                branch_code: yup.string().required('Branch code is required'),
                email: yup.string().test('email-optional', 'Invalid email address', value => !value || emailRegex.test(value)),
                phone_number: yup.string().test('phone-optional', 'Invalid phone number', value => !value || phoneRegex.test(value)),
                provider_number: yup.string().required('Branch NPI is required'),
                umpi: yup
                    .string()
                    .trim()
                    .test('umpi', 'Branch UMPI must be letters and digits, at most 20', value => !value || /^[A-Za-z0-9]{1,20}$/.test(value)),
                address: yup.object().shape({
                    street1: yup.string().required('Street is required'),
                    street2: yup.string(),
                    city: yup.string().required('City is required'),
                    state: yup.string().required('State is required'),
                    zip: yup.string().required('ZIP code is required'),
                }),
            }),
        ),
    })

    useEffect(() => {
        adminApi
            .getOrganization(organizationId)
            .then(({data: {data}}) => setOrganization(data))
            .catch(() => {})

        loadServiceCatalog()
            .then(setServiceCatalog)
            .catch(error => {
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }, [organizationId])

    useEffect(() => {
        if (isEdit) {
            setSkeleton(true)
            adminApi
                .getBranch(organizationId, branchId)
                .then(({data: {data: branch}}) => {
                    setSkeleton(false)
                    const address = branch?.addresses?.[0]
                    form.reset({
                        branch_name: branch?.branch_name || '',
                        branch_code: branch?.branch_code || '',
                        provider_number: branch?.provider_number || '',
                        umpi: branch?.umpi || '',
                        email: branch?.email || '',
                        phone_number: branch?.phone_number || '',
                        address: {
                            street1: address?.street_line1 || '',
                            street2: address?.street_line2 || '',
                            city: address?.city || '',
                            state: address?.state || '',
                            zip: address?.zip_code || '',
                        },
                    })
                    setServices(branch?.services || [])
                    setBranchStatus(branch?.status || 'active')
                })
                .catch(error => {
                    setSkeleton(false)
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                })
        }
    }, [organizationId, branchId])

    const cancel = () => navigate(`/admin/organizations/${organizationId}`)

    const selectAddress = selected => {
        form.setValue('address.street2', '')
        form.setValue('address.city', selected?.city)
        form.setValue('address.state', selected?.state)
        form.setValue('address.zip', selected?.postcode)
    }

    const addService = () => {
        if (!selectedServiceId) return
        const catalogService = serviceCatalog.find(item => item.id === selectedServiceId)
        if (!catalogService) return
        setServices(prev => [
            ...prev,
            {
                id: catalogService.id,
                code: catalogService.code,
                name: catalogService.name,
                category: catalogService.category,
                service_type: catalogService.service_type,
                evv_required: catalogService.requires_evv,
                billing_code: catalogService.hcpc_code,
                billing_unit: catalogService.billing_unit,
                description: catalogService.description,
                is_active: true,
            },
        ])
        setSelectedServiceId('')
    }

    const removeService = index => setServices(prev => prev.filter((_, i) => i !== index))

    const submit = data => {
        const payload = {
            branch_name: data.branch_name,
            branch_code: data.branch_code,
            provider_number: data.provider_number,
            umpi: data.umpi?.trim() || '',
            email: data.email,
            phone_number: data.phone_number,
            status: branchStatus,
            addresses: [
                {
                    // A branch address is an office. 'primary' is the clients'
                    // vocabulary; branch_addresses only holds 'office' or
                    // 'facility', and sending anything else is refused.
                    address_type: 'office',
                    street_line1: data.address.street1,
                    street_line2: data.address.street2,
                    city: data.address.city,
                    state: data.address.state,
                    zip_code: data.address.zip,
                },
            ],
            services: services.map(service => ({service_id: service.id, is_active: service.is_active !== false})),
        }

        const toast = isEdit
            ? {
                  status: 'success',
                  title: 'Organization Branch Updated Successfully',
                  description: 'This organization branch has been updated successfully.',
              }
            : {
                  status: 'success',
                  title: 'Organization Branch Added Successfully',
                  description: 'This organization branch has been added successfully.',
              }

        setLoading(true)
        const request = isEdit ? adminApi.updateBranch(organizationId, branchId, payload) : adminApi.createBranch(organizationId, payload)

        request
            .then(() => {
                setLoading(false)
                navigate(`/admin/organizations/${organizationId}`, {state: {toast, tab: 'branch'}})
            })
            .catch(error => {
                setLoading(false)
                const {title, description} = parseError(error)
                setMessageBox({
                    status: 'error',
                    title: title || (isEdit ? 'Branch Update Failed' : 'Branch Add Failed'),
                    description: description || 'There was an error saving this branch. Please try again later.',
                })
            })
    }

    return (
        <>
            {mobile ? (
                <>
                    <div className="header">
                        <IconButton onClick={cancel}>
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="h6" color="text.primary">
                            {isEdit ? 'Edit Branch' : 'Add New Branch'}
                        </Typography>
                    </div>
                    <div className="actions">
                        <Button variant="outlined" onClick={cancel} style={{flex: 1}}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={form.handleSubmit(submit)} style={{flex: 1}}>
                            {isEdit ? 'Save Changes' : 'Add Branch'}
                        </Button>
                    </div>
                    <br />
                </>
            ) : (
                <>
                    <Typography variant="body1" color="text.secondary">
                        <LinkRoute className="link" to="/admin/organizations">
                            Organizations
                        </LinkRoute>
                        {' / '}
                        <LinkRoute className="link" to={`/admin/organizations/${organizationId}`}>
                            {organization?.name || 'Organization'}
                        </LinkRoute>
                        {isEdit ? ' / Edit branch' : ' / Add branch'}
                    </Typography>
                    <br />
                    <div className="header">
                        <div className="back-container">
                            <div className="back-button" onClick={cancel}>
                                <ArrowBack style={{color: '#ffffff'}} fontSize="small" />
                            </div>
                            <Typography variant="h5" color="text.primary">
                                {isEdit ? 'Edit Branch' : 'Add New Branch'}
                            </Typography>
                        </div>
                        <div className="actions">
                            <Button variant="outlined" onClick={cancel}>
                                Cancel
                            </Button>
                            <Button variant="contained" onClick={form.handleSubmit(submit)}>
                                {isEdit ? 'Save Changes' : 'Add Branch'}
                            </Button>
                        </div>
                    </div>
                </>
            )}
            <br />
            <br />
            {skeleton && <FormSkeleton mobile={mobile} />}
            <div style={{display: skeleton ? 'none' : 'block'}}>
                <Typography variant="overline" color="text.secondary">
                    BRANCH INFORMATION
                </Typography>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Branch Name{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <TextField
                    placeholder="Enter the branch name"
                    fullWidth
                    {...form.register('branch_name')}
                    error={!!form.formState.errors?.branch_name}
                    helperText={form.formState.errors?.branch_name?.message}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Branch Code{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <TextField
                    placeholder="Enter a unique branch code"
                    fullWidth
                    {...form.register('branch_code')}
                    error={!!form.formState.errors?.branch_code}
                    helperText={form.formState.errors?.branch_code?.message}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Branch Email
                </Typography>
                <TextField
                    placeholder="Enter the branch email"
                    fullWidth
                    {...form.register('email')}
                    error={!!form.formState.errors?.email}
                    helperText={form.formState.errors?.email?.message}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Branch Phone Number
                </Typography>
                <TextField
                    placeholder="Enter the branch phone number"
                    fullWidth
                    {...form.register('phone_number')}
                    error={!!form.formState.errors?.phone_number}
                    helperText={form.formState.errors?.phone_number?.message}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Branch NPI{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <TextField
                    placeholder="Enter the branch NPI"
                    fullWidth
                    {...form.register('provider_number')}
                    error={!!form.formState.errors?.provider_number}
                    helperText={form.formState.errors?.provider_number?.message}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Branch UMPI
                </Typography>
                <TextField
                    placeholder="Enter the branch UMPI"
                    fullWidth
                    {...form.register('umpi')}
                    error={!!form.formState.errors?.umpi}
                    helperText={form.formState.errors?.umpi?.message}
                />
                <br />
                <br />
                <Typography variant="overline" color="text.secondary">
                    ADDRESS
                </Typography>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Street Address Line 1{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <AddressAutocompleteField
                    control={form.control}
                    name="address.street1"
                    error={!!form.formState.errors?.address?.street1}
                    helperText={form.formState.errors?.address?.street1?.message}
                    onSelect={selectAddress}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Street Address Line 2
                </Typography>
                <TextField placeholder="Enter your street address line 2" fullWidth {...form.register('address.street2')} />
                <br />
                <br />
                <div className="form">
                    <div className="form-left">
                        <Typography variant="inputLabel" color="text.secondary">
                            City{' '}
                            <Typography variant="inputLabel" color="error">
                                *
                            </Typography>
                        </Typography>
                        <TextField
                            placeholder="Enter your city"
                            fullWidth
                            {...form.register('address.city')}
                            error={!!form.formState.errors?.address?.city}
                            helperText={form.formState.errors?.address?.city?.message}
                        />
                    </div>
                    <div className="form-right">
                        <Typography variant="inputLabel" color="text.secondary">
                            State{' '}
                            <Typography variant="inputLabel" color="error">
                                *
                            </Typography>
                        </Typography>
                        <FormControl fullWidth error={!!form.formState.errors?.address?.state}>
                            <Controller
                                name="address.state"
                                control={form.control}
                                render={({field}) => (
                                    <Select
                                        {...field}
                                        value={field.value ?? ''}
                                        displayEmpty
                                        renderValue={selected =>
                                            selected === '' ? <span style={{color: '#aaa'}}>Select your state</span> : selected
                                        }
                                        MenuProps={{
                                            PaperProps: {style: {maxHeight: 300}},
                                            anchorOrigin: {vertical: 'top', horizontal: 'left'},
                                            transformOrigin: {vertical: 'bottom', horizontal: 'left'},
                                        }}
                                    >
                                        {usStates.map(item => (
                                            <MenuItem key={item} value={item}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            <FormHelperText>{form.formState.errors?.address?.state?.message}</FormHelperText>
                        </FormControl>
                    </div>
                </div>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Zip Code{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <TextField
                    placeholder="Enter your zip code"
                    fullWidth
                    {...form.register('address.zip')}
                    error={!!form.formState.errors?.address?.zip}
                    helperText={form.formState.errors?.address?.zip?.message}
                />
                <br />
                <br />
                <br />
                <Typography variant="overline" color="text.secondary">
                    SERVICES
                </Typography>
                <br />
                <br />
                <div className="services-form">
                    <div className="service-select">
                        <Typography variant="inputLabel" color="text.secondary">
                            Service{' '}
                            <Typography variant="inputLabel" color="error">
                                *
                            </Typography>
                        </Typography>
                        <Select
                            fullWidth
                            displayEmpty
                            value={selectedServiceId}
                            onChange={e => setSelectedServiceId(e.target.value)}
                            renderValue={selected => {
                                if (!selected) return <span style={{color: '#aaa'}}>Select the service</span>
                                return serviceCatalog.find(item => item.id === selected)?.name
                            }}
                            MenuProps={{
                                PaperProps: {style: {maxHeight: 320}},
                                anchorOrigin: {vertical: 'top', horizontal: 'left'},
                                transformOrigin: {vertical: 'bottom', horizontal: 'left'},
                            }}
                        >
                            {serviceCatalog.map(item => (
                                <MenuItem key={item.id} value={item.id}>
                                    <div className="service-option">
                                        <Chip
                                            label={item.billing_unit?.replace(/_/g, ' ')?.toUpperCase()}
                                            size="small"
                                            style={{fontWeight: 'bold', backgroundColor: '#0000000A'}}
                                        />
                                        <Typography variant="body2">{item.name}</Typography>
                                    </div>
                                </MenuItem>
                            ))}
                        </Select>
                    </div>
                    <Button variant="outlined" startIcon={<Add />} onClick={addService} className="add-button">
                        Add
                    </Button>
                </div>
                <br />
                {services.map((service, index) => (
                    <div key={`${service.id}-${index}`} className="service-row">
                        <div>
                            <Typography variant="h9" color="primary">
                                {service.name}
                            </Typography>
                            <br />
                            <div className="service-meta-row">
                                <Chip
                                    label={service.category?.replace(/_/g, ' ')?.toUpperCase()}
                                    size="small"
                                    style={{fontWeight: 'bold', backgroundColor: '#0000000A'}}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    {service.billing_code || service.description}
                                </Typography>
                            </div>
                            <div style={{marginTop: 6}}>{evvChip(service.evv_required)}</div>
                        </div>
                        <IconButton color="error" onClick={() => removeService(index)}>
                            <Delete />
                        </IconButton>
                    </div>
                ))}
            </div>
            <br />
            <br />
            <br />
            <br />
        </>
    )
}
